import {
  TRIVIA_CHOICE_MAX_CHARS,
  TRIVIA_MAX_CHOICES,
  TRIVIA_MAX_TIMER_S,
  TRIVIA_MIN_CHOICES,
  TRIVIA_MIN_TIMER_S,
  TRIVIA_QUESTION_MAX_CHARS,
} from '../constants.ts';

export type TriviaQuestionDraft = {
  id: string;
  text: string;
  timerMs?: number | null;
  correctChoiceId?: string | null;
  choices: Array<{ id: string; text: string }>;
};

export type TriviaConfigDraft = {
  questions?: TriviaQuestionDraft[] | null;
};

export type QuestionErrorField = 'text' | 'choices' | 'correct' | 'timer';

export type QuestionFieldError = {
  index: number;
  field: QuestionErrorField;
  message: string;
};

export function questionTextError(
  question: TriviaQuestionDraft,
  index: number,
) {
  const n = index + 1;
  if (!question.text.trim()) {
    return `Question ${n} needs question text`;
  }
  if (question.text.length > TRIVIA_QUESTION_MAX_CHARS) {
    return `Question ${n} must be ${TRIVIA_QUESTION_MAX_CHARS} characters or fewer`;
  }
  return null;
}

export function questionChoicesError(
  question: TriviaQuestionDraft,
  index: number,
) {
  const n = index + 1;
  const filled = question.choices.filter((choice) => choice.text.trim());
  if (
    filled.length < TRIVIA_MIN_CHOICES ||
    filled.length > TRIVIA_MAX_CHOICES
  ) {
    return `Question ${n} must have ${TRIVIA_MIN_CHOICES} to ${TRIVIA_MAX_CHOICES} choices`;
  }
  if (filled.some((choice) => choice.text.length > TRIVIA_CHOICE_MAX_CHARS)) {
    return `Question ${n} choices must be ${TRIVIA_CHOICE_MAX_CHARS} characters or fewer`;
  }
  return null;
}

export function questionCorrectError(
  question: TriviaQuestionDraft,
  index: number,
) {
  const n = index + 1;
  const filled = question.choices.filter((choice) => choice.text.trim());
  if (
    !question.correctChoiceId ||
    !filled.some((choice) => choice.id === question.correctChoiceId)
  ) {
    return `Question ${n} needs a correct answer`;
  }
  return null;
}

export function questionTimerError(
  question: TriviaQuestionDraft,
  index: number,
) {
  if (question.timerMs == null) return null;
  const n = index + 1;
  const seconds = question.timerMs / 1000;
  if (
    !Number.isFinite(seconds) ||
    seconds < TRIVIA_MIN_TIMER_S ||
    seconds > TRIVIA_MAX_TIMER_S
  ) {
    return `Question ${n} timer must be between ${TRIVIA_MIN_TIMER_S} and ${TRIVIA_MAX_TIMER_S} seconds`;
  }
  return null;
}

export function collectQuestionErrors(
  questions: TriviaQuestionDraft[],
): QuestionFieldError[] {
  const errors: QuestionFieldError[] = [];
  questions.forEach((question, index) => {
    const text = questionTextError(question, index);
    if (text) errors.push({ index, field: 'text', message: text });
    const choices = questionChoicesError(question, index);
    if (choices) errors.push({ index, field: 'choices', message: choices });
    const correct = questionCorrectError(question, index);
    if (correct) errors.push({ index, field: 'correct', message: correct });
    const timer = questionTimerError(question, index);
    if (timer) errors.push({ index, field: 'timer', message: timer });
  });
  return errors;
}

export function parseTriviaSaveError(
  message: string,
): QuestionFieldError | null {
  const source = message.replace(/^Couldn't save\. Try again\.\s*/i, '');
  const match = source.match(/Question (\d+)\s(.+)$/i);
  if (!match) return null;
  const index = Number(match[1]) - 1;
  const rest = match[2].toLowerCase();
  let field: QuestionErrorField = 'text';
  if (rest.includes('correct answer')) field = 'correct';
  else if (rest.includes('choice')) field = 'choices';
  else if (rest.includes('timer')) field = 'timer';
  else if (rest.includes('question text') || rest.includes('characters')) {
    field = 'text';
  }
  return { index, field, message };
}

export function errorsForQuestion(
  errors: QuestionFieldError[],
  index: number,
): Partial<Record<QuestionErrorField, string>> {
  const next: Partial<Record<QuestionErrorField, string>> = {};
  for (const error of errors) {
    if (error.index === index && !next[error.field]) {
      next[error.field] = error.message;
    }
  }
  return next;
}

export function firstTriviaSaveErrorMessage(config: TriviaConfigDraft) {
  return collectQuestionErrors(config.questions ?? [])[0]?.message ?? null;
}
